import React, { useEffect, useState } from "react";
import { Modal, Checkbox, Button, Input, Spin, message } from "antd";
import {
  FileTextOutlined,
  MobileOutlined,
  SafetyOutlined,
  CheckCircleOutlined,
  LockOutlined
} from "@ant-design/icons";
import { toast } from "react-toastify";

const ConsentModal = ({
  visible,
  onClose,
  patientId,
  mobileNumber,
  serviceType = "APPT_SERVICE",
  onConsentComplete,
}) => {
  const [loading, setLoading] = useState(false);
  const [consentChecked, setConsentChecked] = useState({
    privacy: false,
    terms: false,
  });
  const [consentVersions, setConsentVersions] = useState([]);
  const [consentUrls, setConsentUrls] = useState({});
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [privacyVersion, setPrivacyVersion] = useState("");
  const [termsVersion, setTermsVersion] = useState("");
  const [timer, setTimer] = useState(0);

  const API_URL = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("auth_token");
  const tenantId = localStorage.getItem("tenantId");

  // 1. Check if consent already exists
  useEffect(() => {
    if (!visible || !patientId) return;
    const checkConsent = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${API_URL}patientConsent/by-patient?patientId=${patientId}&serviceType=${serviceType}&Status=1`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              tenantId: tenantId,
            },
          }
        );
        const data = await res.json();
        if (Array.isArray(data.data) && data.data.length > 0) {
          // Consent already exists
          message.success("Consent already provided.");
          setLoading(false);
          onConsentComplete && onConsentComplete();
        } else {
          // Fetch consent versions
          fetchConsentVersions();
        }
      } catch (err) {
        message.error("Failed to check consent status.");
        setLoading(false);
      }
    };
    checkConsent();
    // eslint-disable-next-line
  }, [visible, patientId]);

  // 2. Fetch consent versions
  const fetchConsentVersions = async () => {
    try {
      const res = await fetch(
        `${API_URL}consentVersion/by-ServiceType?serviceType=${serviceType}&Status=1`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            // tenantId: tenantId,
          },
        }
      );
      const data = await res.json();
      if (Array.isArray(data.data)) {
        setConsentVersions(data.data);
        // Fetch signed URLs for each consent file
        const urls = {};
        for (const consent of data.data) {
          const urlRes = await fetch(
            `${API_URL}consentVersion/file-url?fileName=${consent.consentFile}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          const urlData = await urlRes.json();
          urls[consent.consentName] = urlData.signedUrl;
          if (consent.consentName === "PrivacyPolicy") setPrivacyVersion(consent.versionNumber);
          if (consent.consentName === "TermsOfUse") setTermsVersion(consent.versionNumber);
        }
        setConsentUrls(urls);
      }
    } catch (err) {
      message.error("Failed to fetch consent versions.");
    }
    setLoading(false);
  };

  // 3. Send OTP
  const handleSendOtp = async () => {
    setLoading(true);
    // Clear the OTP input when resending
    setOtp("");
    try {
      const res = await fetch(
        `${API_URL}patientConsent/consent-otp?mobileNumber=${mobileNumber}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            tenantId: tenantId,
          },
        }
      );
      const data = await res.json();
      if (data.status === 200) {
        setOtpSent(true);
        setTimer(60);
        const countdown = setInterval(() => {
          setTimer((prevTimer) => {
            if (prevTimer <= 1) {
              clearInterval(countdown);
              return 0;
            }
            return prevTimer - 1;
          });
        }, 1000);
        toast.success("OTP sent to mobile number");
      } else {
        toast.error("Failed to send OTP");
      }
    } catch (err) {
      message.error("Failed to send OTP");
    }
    setLoading(false);
  };

  // 4. Verify OTP
  // const handleVerifyOtp = async () => {
  //   setVerifyingOtp(true);
  //   try {
  //     const res = await fetch(
  //       `${API_URL}patientConsent/verify-otp?mobileNumber=${mobileNumber}&otp=${otp}`,
  //       {
  //         method: "POST",
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //         },
  //       }
  //     );
  //     const data = await res.json();
  //     if (data.status === 200) {
  //       message.success("OTP verified");
  //       handleSubmitConsent();
  //     } else {
  //       message.error("Invalid OTP");
  //     }
  //   } catch (err) {
  //     message.error("Failed to verify OTP");
  //   }
  //   setVerifyingOtp(false);
  // };
  const handleVerifyOtp = async () => {
    setVerifyingOtp(true);
    // try {
    const res = await fetch(
      `${API_URL}patientConsent/verify-otp?mobileNumber=${mobileNumber}&otp=${otp}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          tenantId: tenantId,
        },
      }
    );

    const data = await res.json();
    console.log(data, "fgtyhujik")
    if (data.status == 200) {
      // message.success("OTP verified");
      toast.success("OTP verified");
      handleSubmitConsent();
    } else {
      console.log(data?.error || "Invalid OTP"); // ✅ Show server error message
      toast.error(data?.error || "Invalid OTP");
      // Reset timer to enable resend button immediately on wrong OTP
      setTimer(0);
      setOtp(""); // Clear the OTP input
    }
    // } catch (err) {
    //   console.error("Failed to verify OTP");
    // }
    setVerifyingOtp(false);
  };


  // 5. Submit Consent
  const handleSubmitConsent = async () => {
    setSubmitting(true);

    const now = new Date().toISOString().slice(0, 19).replace("T", " ");
    const payload = {
      patientId,
      privacyPolicyConsent: 1,
      termsAndConditionConsent: 1,
      privacyVersionNumber: privacyVersion,
      tcVersionNumber: termsVersion,
      serviceType,
      status: 1,
      createdDate: now,
      modifiedDate: now,
      createdBy: patientId,
      modifiedBy: patientId,
    };

    try {
      const response = await fetch(`${API_URL}patientConsent/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          tenantId: tenantId,
          },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      console.log(result, "result");
      if (result.statusCode == 200) {
        toast.success("Consent saved successfully");
        onConsentComplete && onConsentComplete();
      } else {
        toast.error(result.message || "Something went wrong while sending Notification ");
      }
    } catch (error) {
      console.log(error, "fcgvhbj")
      //  toast.error("Something went wrong while submitting order");
    }
    //     try {
    //   const res = await fetch(
    //     `${API_URL}patientConsent/create`,
    //     {
    //       method: "POST",
    //       headers: {
    //         "Content-Type": "application/json",
    //         Authorization: `Bearer ${token}`,
    //       },
    //       body: JSON.stringify(payload),
    //     }
    //   );
    //   const data = await res.json();
    //   if (data.statusCode === 200) {
    //     message.success("Consent saved successfully");
    //     onConsentComplete && onConsentComplete();
    //     onClose && onClose();
    //   } else {
    //     message.error("Failed to save consent");
    //   }
    //   console.log(data,"data")
    // } catch (err) {
    //   console.log(err,"srdtfyguhj")
    //   message.error("Failed to save consent");
    //   //  toast.error(err);
    // }
    setSubmitting(false);
  };

  const canSendOtp = consentChecked.privacy && consentChecked.terms && !otpSent;
  const canVerifyOtp = otpSent && otp.length === 6;

  // Handle skip functionality
  const handleSkip = () => {
    onClose && onClose();
  };

  const modalStyles = {
    mask: {
      backgroundColor: 'rgba(0, 0, 0, 0.65)',
      backdropFilter: 'blur(4px)',
    },
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}

      maskClosable={false}
      width={520}
      centered
      styles={modalStyles}
      className="modern-consent-modal"
    >
      <style jsx>{`
        .modern-consent-modal .ant-modal-content {
          border-radius: 16px !important;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
          overflow: hidden;
        }
        .modern-consent-modal .ant-modal-header {
          border-bottom: 1px solid #f1f5f9 !important;
          padding: 24px 24px 20px 24px !important;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
        }
        .modern-consent-modal .ant-modal-body {
          padding: 32px 24px 24px 24px !important;
          background: #ffffff;
        }
        .consent-card {
          background: #ffffff;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          padding: 20px;
          margin-bottom: 16px;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }
        .consent-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #004172 0%, #3cba84 100%);
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .consent-card:hover {
          border-color: #004172;
          box-shadow: 0 8px 25px -8px rgba(0, 65, 114, 0.25);
        }
        .consent-card:hover::before {
          opacity: 1;
        }
        .consent-checkbox {
          margin-bottom: 0 !important;
        }
        .consent-checkbox .ant-checkbox {
          transform: scale(1.2);
        }
        .consent-checkbox .ant-checkbox-checked .ant-checkbox-inner {
          background: linear-gradient(135deg, #004172 0%, #3cba84 100%) !important;
          border-color: #004172 !important;
        }
        .consent-link {
          color: #004172 !important;
          font-weight: 600;
          text-decoration: none;
          position: relative;
        }
        .consent-link:hover {
          color: #003658 !important;
        }
        .consent-link::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, #004172 0%, #3cba84 100%);
          transform: scaleX(0);
          transition: transform 0.3s ease;
        }
        .consent-link:hover::after {
          transform: scaleX(1);
        }
        .otp-section {
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          border-radius: 12px;
          padding: 24px;
          margin-top: 24px;
          border: 1px solid #e2e8f0;
        }
        .otp-input {
          text-align: center !important;
          font-size: 18px !important;
          font-weight: 600 !important;
          letter-spacing: 2px !important;
          border-radius: 8px !important;
          border: 2px solid #e2e8f0 !important;
          padding: 12px !important;
          margin-bottom: 16px !important;
        }
        .otp-input:focus {
          border-color: #004172 !important;
          box-shadow: 0 0 0 3px rgba(0, 65, 114, 0.1) !important;
        }
        .modern-button {
          height: 48px !important;
          border-radius: 12px !important;
          font-weight: 600 !important;
          font-size: 16px !important;
          border: none !important;
          background: linear-gradient(135deg, #004172 0%, #3cba84 100%) !important;
          box-shadow: 0 4px 15px rgba(0, 65, 114, 0.3) !important;
          transition: all 0.3s ease !important;
        }
        .modern-button:hover {
          transform: translateY(-2px) !important;
          box-shadow: 0 8px 25px rgba(0, 65, 114, 0.4) !important;
        }
        .modern-button:disabled {
          background: #e2e8f0 !important;
          box-shadow: none !important;
          transform: none !important;
          color: #94a3b8 !important;
        }
        .skip-button {
          height: 48px !important;
          border-radius: 12px !important;
          font-weight: 600 !important;
          font-size: 16px !important;
          border: 2px solid #e2e8f0 !important;
          background: #ffffff !important;
          color: #64748b !important;
          margin-right: 12px !important;
          transition: all 0.3s ease !important;
        }
        .skip-button:hover {
          border-color: #cbd5e1 !important;
          color: #475569 !important;
          background: #f8fafc !important;
        }
        .security-note {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          background: #f0f9ff;
          border: 1px solid #bae6fd;
          border-radius: 8px;
          margin-top: 16px;
          color: #0369a1;
          font-size: 14px;
        }
      `}</style>

      {loading ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '60px 20px',
          gap: '16px'
        }}>
          <Spin size="large" />
          <p style={{ color: '#64748b', margin: 0 }}>Loading consent information...</p>
        </div>
      ) : (
        <div>
          <div style={{ marginBottom: '24px' }}>
            <p style={{
              color: '#475569',
              fontSize: '16px',
              lineHeight: '1.6',
              margin: '0 0 24px 0'
            }}>
              To proceed with your appointment, please review and accept our privacy policy and terms of service. Your consent helps us provide you with the best healthcare experience.
            </p>
          </div>

          <div className="consent-card">
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                background: consentChecked.privacy ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : '#f1f5f9',
                color: consentChecked.privacy ? 'white' : '#64748b',
                flexShrink: 0
              }}>
                {consentChecked.privacy ? <CheckCircleOutlined /> : <LockOutlined />}
              </div>
              <div style={{ flex: 1 }}>
                <Checkbox
                  className="consent-checkbox"
                  checked={consentChecked.privacy}
                  onChange={e => setConsentChecked(c => ({ ...c, privacy: e.target.checked }))}
                >
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>
                    I have read and accept the{" "}
                    <a
                      href={consentUrls.PrivacyPolicy}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="consent-link"
                    >
                      Privacy Policy
                    </a>
                  </span>
                </Checkbox>
                <p style={{
                  margin: '8px 0 0 0',
                  color: '#64748b',
                  fontSize: '14px',
                  lineHeight: '1.5'
                }}>
                  Learn how we protect and handle your personal health information
                </p>
              </div>
            </div>
          </div>

          <div className="consent-card">
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                background: consentChecked.terms ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : '#f1f5f9',
                color: consentChecked.terms ? 'white' : '#64748b',
                flexShrink: 0
              }}>
                {consentChecked.terms ? <CheckCircleOutlined /> : <FileTextOutlined />}
              </div>
              <div style={{ flex: 1 }}>
                <Checkbox
                  className="consent-checkbox"
                  checked={consentChecked.terms}
                  onChange={e => setConsentChecked(c => ({ ...c, terms: e.target.checked }))}
                >
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>
                    I have read and accept the{" "}
                    <a
                      href={consentUrls.TermsOfUse}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="consent-link"
                    >
                      Terms & Conditions
                    </a>
                  </span>
                </Checkbox>
                <p style={{
                  margin: '8px 0 0 0',
                  color: '#64748b',
                  fontSize: '14px',
                  lineHeight: '1.5'
                }}>
                  Understand the terms governing our healthcare services
                </p>
              </div>
            </div>
          </div>

          {!otpSent && (
            <>
              {serviceType !== 'CUREBAY_SERVICE' ? (
                <div style={{ display: 'flex', gap: '12px' }}>
                  <Button
                    onClick={handleSkip}
                    className="skip-button"
                    style={{ flex: '0 0 auto', width: '120px' }}
                  >
                    Skip
                  </Button>
                  <Button
                    type="primary"
                    disabled={!canSendOtp}
                    onClick={handleSendOtp}
                    className="modern-button"
                    icon={<MobileOutlined />}
                    style={{ flex: 1 }}
                  >
                    Send Verification Code
                  </Button>
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Button
                    type="primary"
                    disabled={!canSendOtp}
                    onClick={handleSendOtp}
                    className="modern-button"
                    icon={<MobileOutlined />}
                  >
                    Send Verification Code
                  </Button>
                </div>
              )}
              <div className="security-note">
                <SafetyOutlined />
                <span>We'll send a secure verification code to {mobileNumber}</span>
              </div>
            </>
          )}

          {otpSent && (
            <div className="otp-section">
              <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '56px',
                  height: '56px',
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #004172 0%, #3cba84 100%)',
                  color: 'white',
                  marginBottom: '12px'
                }}>
                  <MobileOutlined style={{ fontSize: '24px' }} />
                </div>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>
                  Verification Code Sent
                </h3>
                <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
                  Enter the 6-digit code sent to {mobileNumber}
                </p>
              </div>

              <Input
                placeholder="Enter 6-digit code"
                value={otp}
                maxLength={6}
                onChange={e => setOtp(e.target.value.replace(/\D/g, ""))}
                className="otp-input"
              />

              <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                {timer > 0 ? (
                  <span style={{ color: '#64748b', fontSize: '14px' }}>
                    Resend OTP in {timer} seconds
                  </span>
                ) : (
                  <Button
                    type="link"
                    onClick={handleSendOtp}
                    style={{ padding: 0, height: 'auto', color: '#004172', fontWeight: '600' }}
                  >
                    Resend OTP
                  </Button>
                )}
              </div>

              <Button
                type="primary"
                loading={verifyingOtp || submitting}
                disabled={!canVerifyOtp}
                onClick={handleVerifyOtp}
                block
                className="modern-button"
                icon={<CheckCircleOutlined />}
              >
                {verifyingOtp ? 'Verifying...' : submitting ? 'Saving Consent...' : 'Verify & Complete'}
              </Button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default ConsentModal;